/**
 * Form Validation Utilities
 *
 * Centralized validation functions to ensure consistency across all forms.
 * These validators catch common edge cases and provide user-friendly error messages.
 */

/**
 * Validates email addresses using a robust regex pattern.
 *
 * This pattern catches common mistakes like:
 * - Double dots (..)
 * - Missing @ symbol
 * - Invalid characters
 * - Missing or invalid TLD
 * - Leading/trailing dots
 *
 * @param email - The email address to validate
 * @returns Error message if invalid, undefined if valid
 *
 * @example
 * validateEmail("user@example.com") // undefined (valid)
 * validateEmail("user@example..com") // "Invalid email format" (double dots)
 * validateEmail("user@.com") // "Invalid email format" (missing domain)
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email) return "Email is required";

  // Trim whitespace
  const trimmedEmail = email.trim();

  // More robust email regex that catches common edge cases
  // This pattern ensures:
  // - No consecutive dots
  // - Valid characters in local part (before @), including + for email tagging
  // - Valid domain structure
  // - Valid TLD (at least 2 characters)
  const emailRegex =
    /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

  // Additional checks for edge cases the regex might miss
  if (trimmedEmail.includes("..")) {
    return "Invalid email format";
  }

  if (trimmedEmail.startsWith(".") || trimmedEmail.includes("@.")) {
    return "Invalid email format";
  }

  if (!emailRegex.test(trimmedEmail)) {
    return "Please enter a valid email address";
  }

  return undefined;
};

/**
 * Validates a person's name.
 *
 * @param name - The name to validate
 * @param minLength - Minimum length (default: 2)
 * @returns Error message if invalid, undefined if valid
 */
export const validateName = (
  name: string,
  minLength = 2
): string | undefined => {
  if (!name) return "Name is required";

  const trimmedName = name.trim();

  if (trimmedName.length < minLength) {
    return `Name must be at least ${minLength} characters`;
  }

  // Check for valid name characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(trimmedName)) {
    return "Name can only contain letters, spaces, hyphens, and apostrophes";
  }

  return undefined;
};

/**
 * Validates a phone number.
 *
 * Accepts various formats:
 * - +1234567890
 * - (123) 456-7890
 * - 123-456-7890
 * - 123 456 7890
 *
 * @param phoneNumber - The phone number to validate
 * @param required - Whether the field is required (default: false)
 * @returns Error message if invalid, undefined if valid
 */
export const validatePhoneNumber = (
  phoneNumber: string,
  required = false
): string | undefined => {
  if (!phoneNumber) {
    return required ? "Phone number is required" : undefined;
  }

  const trimmedPhone = phoneNumber.trim();

  // Remove all non-digit characters to check length
  const digitsOnly = trimmedPhone.replace(/\D/g, "");

  // Most phone numbers are 10-15 digits (accounting for country codes)
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return "Please enter a valid phone number";
  }

  // Accept various formats
  const phoneRegex =
    /^[+]?[(]?[0-9]{1,4}[)]?[\s.-]?[(]?[0-9]{1,4}[)]?[\s.-]?[0-9]{1,9}$/;
  if (!phoneRegex.test(trimmedPhone)) {
    return "Please enter a valid phone number";
  }

  return undefined;
};

/**
 * Validates a password for minimum security requirements.
 *
 * Requirements:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 *
 * @param password - The password to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) return "Password is required";

  if (password.length < 8) {
    return "Password must be at least 8 characters";
  }

  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return "Password must contain uppercase, lowercase, and a number";
  }

  return undefined;
};

/**
 * Validates that two passwords match.
 *
 * @param confirmPassword - The confirmation password
 * @param originalPassword - The original password to match against
 * @returns Error message if invalid, undefined if valid
 */
export const validatePasswordMatch = (
  confirmPassword: string,
  originalPassword: string
): string | undefined => {
  if (!confirmPassword) return "Please confirm your password";

  if (confirmPassword !== originalPassword) {
    return "Passwords do not match";
  }

  return undefined;
};

/**
 * Validates that a required field has a value.
 *
 * @param value - The value to check
 * @param fieldName - The name of the field for error messages
 * @returns Error message if invalid, undefined if valid
 */
export const validateRequired = (
  value: string | string[] | undefined | null,
  fieldName: string
): string | undefined => {
  if (!value) {
    return `${fieldName} is required`;
  }

  if (Array.isArray(value) && value.length === 0) {
    return `Please select at least one ${fieldName.toLowerCase()}`;
  }

  if (typeof value === "string" && value.trim().length === 0) {
    return `${fieldName} is required`;
  }

  return undefined;
};
